import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BanknoteIcon, CalendarPlusIcon, CheckIcon, ChevronRightIcon, ClockIcon, FolderOpenIcon, MapPinIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { missingFields } from '../../utils/personal';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Donut } from '../../components/ui/Donut';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Waves } from '../../components/ui/Logo';
import { AbsenceModal } from '../../components/member/AbsenceModal';
import { attendanceSummary } from '../../utils/attendance';
import { eventTitle, eventTypeClass, eventTypeShort, formatDate, formatShort, priorityClass, priorityLabel, relativeDays } from '../../utils/labels';
const donutColor: Record<string, string> = {
  zelena: '#007c69',
  oranzna: '#d97706',
  rdeca: '#e11d48',
  premalo: '#cbd5e1'
};
export function MemberHome() {
  const {
    currentMember,
    upcoming,
    visibleEvents,
    getRsvp,
    setRsvp,
    settings,
    announcements,
    fees,
    today,
    isExpected,
    pushToast,
    rooms,
    transport
  } = useApp();
  const navigate = useNavigate();
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const me = currentMember!;
  const jeGost = me.type === 'gostujoci' || me.type === 'castni';
  const mojiDogodki = useMemo(() => visibleEvents(me.id).filter(e => upcoming.some(u => u.id === e.id)), [visibleEvents, upcoming, me.id]);
  const naslednji = mojiDogodki[0];
  const rsvp = naslednji ? getRsvp(naslednji.id) : undefined;
  const potovalnaOpozorila = useMemo(() => {
    return mojiDogodki.filter(e => e.potovanje?.aktivno && isExpected(e, me.id)).flatMap(e => {
      const t = e.potovanje!;
      const opozorila: string[] = [];
      if (missingFields(me, t.obveznaPolja).length > 0) {
        opozorila.push('Za gostovanje morate dopolniti osebne podatke.');
      }
      if (t.nastanitev?.aktivno && !rooms[e.id]?.[me.id]) {
        opozorila.push('Soba še ni določena.');
      }
      if (t.prevoz?.aktivno && !transport[e.id]?.[me.id]) {
        opozorila.push('Za gostovanje še nimate urejenega prevoza.');
      }
      return opozorila.map(besedilo => ({
        event: e,
        besedilo
      }));
    });
  }, [mojiDogodki, isExpected, me, rooms, transport]);
  const povzetek = attendanceSummary(me, settings);
  const zadnjeObvestilo = announcements.find(a => a.prejemniki.includes(me.id));
  const neprebrana = announcements.filter(a => a.prejemniki.includes(me.id) && !a.prebrali.includes(me.id)).length;
  const clanarina = fees.find(f => f.memberId === me.id && f.leto === settings.letoClanarine);
  return <div className="space-y-5" data-id="element-1565" data-mp="13qr80kug4a7h">
      <div data-id="element-1566" data-mp="13pg2afhfwsdq">
        <p className="text-sm text-ink-muted" data-id="element-1567" data-mp="3fem7u2frd061">{formatDate(today, {
          weekday: true,
          long: true
        })}</p>
        <h1 className="mt-0.5 text-2xl font-semibold text-ink" data-id="element-1568" data-mp="3k52nlt71wxvk">Pozdravljeni, {me.ime}.</h1>
      </div>

      {naslednji && <Card className="relative overflow-hidden" data-id="element-1569" data-mp="36eqx1yq4776j">
          <Waves className="pointer-events-none absolute -right-10 top-0 h-24 w-72" opacity={0.1} data-id="element-1570" data-mp="36c4mdq7xgbk3" />
          <div className="relative p-5" data-id="element-1571" data-mp="3ad6ocet8b1pr">
            <div className="flex items-center justify-between gap-3" data-id="element-1572" data-mp="1zhmncyc49nr">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1573" data-mp="6yuao10xzmrx">
                Naslednji dogodek
              </p>
              <Badge className="bg-green-50 text-green-700 border-green-200" data-id="element-1574" data-mp="3p3npry5v5rmn">
                {relativeDays(naslednji.datum, today)}
              </Badge>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-1575" data-mp="2dp0py9mwh5jx">
              <Badge className={eventTypeClass[naslednji.type]} data-id="element-1576" data-mp="2iodagnqau0jc">{eventTypeShort[naslednji.type]}</Badge>
              {naslednji.opomba && <Badge className="bg-canvas text-ink-muted border-line" data-id="element-1577" data-mp="2xz1thc6pgu2w">{naslednji.opomba}</Badge>}
            </div>

            <h2 className="mt-2 text-xl font-semibold text-ink" data-id="element-1578" data-mp="2t5yweu2pz2hy">{eventTitle(naslednji)}</h2>
            <div className="mt-2 space-y-1 text-sm text-ink-muted" data-id="element-1579" data-mp="2o8gp184covc2">
              <p className="flex items-center gap-1.5" data-id="element-1580" data-mp="2k7n14eqwrgj6">
                <ClockIcon className="h-4 w-4" data-id="element-1581" data-mp="3gww1bmw3kzjp" />
                {formatDate(naslednji.datum, {
              weekday: true
            })} · {naslednji.zacetek}
                {naslednji.konec ? `–${naslednji.konec}` : ''}
              </p>
              <p className="flex items-center gap-1.5" data-id="element-1582" data-mp="37vxw519xalzj">
                <MapPinIcon className="h-4 w-4" data-id="element-1583" data-mp="37ud1zcrit527" />
                {naslednji.lokacija}
              </p>
            </div>

            {isExpected(naslednji, me.id) ? <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-1584" data-mp="1wi2dvjnq1x43">
                <Button variant={rsvp?.status === 'pridem' ? 'primary' : 'secondary'} onClick={() => {
            setRsvp(naslednji.id, {
              status: 'pridem'
            });
            pushToast('Zabeleženo — se vidimo na vaji.');
          }} data-id="element-1585" data-mp="1rhy0w3m0vkim">
                  <CheckIcon className="h-4 w-4" data-id="element-1586" data-mp="g0ty8k36f5a6" />
                  Pridem
                </Button>
                <Button variant={rsvp?.status === 'ne_morem' ? 'danger' : 'secondary'} onClick={() => setAbsenceOpen(true)} data-id="element-1587" data-mp="kr21wl6681pk">
                  <XIcon className="h-4 w-4" data-id="element-1588" data-mp="psrfa7gv8rgt" />
                  Ne morem priti
                </Button>
              </div> : <p className="mt-4 rounded-lg bg-canvas px-3 py-2 text-[13px] text-ink-muted" data-id="element-1589" data-mp="zpm9ie63w5pa">
                Za ta dogodek vaša prisotnost ni pričakovana.
              </p>}

            {rsvp?.status === 'ne_morem' && <p className="mt-2 text-[13px] text-ink-muted" data-id="element-1590" data-mp="31hqwdb8r0ct5">
                Sporočili ste odsotnost{rsvp.razlog ? ` — ${rsvp.razlog.toLowerCase()}` : ''}.
              </p>}

            <div className="mt-4 flex items-center justify-between border-t border-line pt-3" data-id="element-1591" data-mp="cnknifxre8q3">
              <Link to={`/clan/dogodki/${naslednji.id}`} className="text-[13px] font-medium text-green-600 hover:underline" data-id="element-1592" data-mp="7piup3aj6v1f">
                Podrobnosti dogodka
              </Link>
              <Link to="/clan/odsotnost" className="flex items-center gap-1.5 text-[13px] font-medium text-ink-muted hover:text-ink" data-id="element-1593" data-mp="hoi6uvui98t6">
                <CalendarPlusIcon className="h-4 w-4" data-id="element-1594" data-mp="qmk4d1f15jbp" />
                Daljša odsotnost
              </Link>
            </div>
          </div>
        </Card>}

      {potovalnaOpozorila.length > 0 && <Card className="border-amber-200 bg-amber-50/60 p-4" data-id="element-1595" data-mp="10eq3cv3p910e">
          <div className="flex items-start gap-3" data-id="element-1596" data-mp="1edfyi56ykqm4">
            <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" data-id="element-1597" data-mp="19dbkcltiw7z2" />
            <div className="min-w-0 flex-1" data-id="element-1598" data-mp="19b8m5ei7v8j9">
              <p className="text-sm font-medium text-ink" data-id="element-1599" data-mp="1sytxqh6z4e65">
                {eventTitle(potovalnaOpozorila[0].event)} — potrebna vaša dopolnitev
              </p>
              <ul className="mt-1 space-y-0.5 text-[13px] text-ink-muted" data-id="element-1600" data-mp="1xvu28no6d8v5">
                {potovalnaOpozorila.slice(0, 3).map(o => <li key={o.besedilo} data-id="element-1601" data-mp="1nz8wg04bztq9">{o.besedilo}</li>)}
              </ul>
              <Link to={`/clan/dogodki/${potovalnaOpozorila[0].event.id}`} className="mt-2 inline-block text-[13px] font-medium text-green-700 hover:underline" data-id="element-1602" data-mp="1a8c66fxvnomd">
                Odpri gostovanje
              </Link>
            </div>
          </div>
        </Card>}

      <div className="grid gap-4 md:grid-cols-2" data-id="element-1603" data-mp="15ake9lx8g2i6">
        <Card className="p-5" data-id="element-1604" data-mp="1576fenns2bfh">
          <div className="flex items-start justify-between gap-3" data-id="element-1605" data-mp="r9hpv2irmg43">
            <div data-id="element-1606" data-mp="w8uoekj01bmi">
              <h3 className="text-base font-semibold text-ink" data-id="element-1607" data-mp="w9ch576gd7po">Moja prisotnost</h3>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1608" data-mp="1bqu8nh77y95y">Sezona 2026/27</p>
            </div>
            {!jeGost && <StatusBadge status={povzetek.status} odstotek={povzetek.odstotek} showPercent={false} data-id="element-1609" data-mp="16s0wbs3o4o7d" />}
          </div>

          <div className="mt-4 flex items-center gap-5" data-id="element-1610" data-mp="11rwpyuwh59uv">
            <Donut odstotek={povzetek.status === 'premalo' ? null : povzetek.odstotek} color={donutColor[povzetek.status]} label="prisotnost" data-id="element-1611" data-mp="11uiwcr7mpjbi" />
            <div className="text-sm" data-id="element-1612" data-mp="su2rpef5g652">
              <p className="text-ink" data-id="element-1613" data-mp="o0rorodgk9if">
                <span className="text-lg font-semibold" data-id="element-1614" data-mp="nzyn31tcwa75">{povzetek.prisoten}</span> od{' '}
                {povzetek.pricakovan} dogodkov
              </p>
              {jeGost ? <p className="mt-1 text-[13px] leading-relaxed text-ink-muted" data-id="element-1615" data-mp="e4eryhmm9fjg">
                  Kot gostujoča članica niste vključeni v semafor prisotnosti.
                </p> : povzetek.status === 'premalo' ? <p className="mt-1 text-[13px] leading-relaxed text-ink-muted" data-id="element-1616" data-mp="54i9wyuqfexd">
                  Semafor se vklopi po {settings.minDogodkov} štetih dogodkih.
                </p> : <p className="mt-1 text-[13px] leading-relaxed text-ink-muted" data-id="element-1617" data-mp="2lqydgpui96p4">
                  Zelena meja je {settings.pragZelena} %.
                </p>}
              <Link to="/clan/prisotnost" className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-green-600 hover:underline" data-id="element-1618" data-mp="2uo8ny8zi0rsz">
                Poglej podrobno
                <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-1619" data-mp="21ys43zpy2j8d" />
              </Link>
            </div>
          </div>
        </Card>

        <div className="space-y-4" data-id="element-1620" data-mp="2bqe9vgjijq3q">
          {zadnjeObvestilo && <Card className="p-5" data-id="element-1621" data-mp="2bsr2cnmwdiv1">
              <div className="flex items-center justify-between gap-3" data-id="element-1622" data-mp="1ebtot5dk9hc1">
                <h3 className="text-base font-semibold text-ink" data-id="element-1623" data-mp="1e9ftcveyy33y">Zadnje obvestilo</h3>
                {neprebrana > 0 && <Badge className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-1624" data-mp="3awacjn510ju1">
                    {neprebrana} neprebrano
                  </Badge>}
              </div>
              <Link to={`/clan/obvestila/${zadnjeObvestilo.id}`} className="mt-3 block group" data-id="element-1625" data-mp="3jwozss1jw906">
                <Badge className={priorityClass[zadnjeObvestilo.prioriteta]} data-id="element-1626" data-mp="3ova0stommwpo">
                  {priorityLabel[zadnjeObvestilo.prioriteta]}
                </Badge>
                <p className="mt-2 text-sm font-semibold text-ink group-hover:text-green-700" data-id="element-1627" data-mp="2r9l092l88mvs">
                  {zadnjeObvestilo.naslov}
                </p>
                <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-ink-muted" data-id="element-1628" data-mp="315o1854cefh5">
                  {zadnjeObvestilo.besedilo}
                </p>
              </Link>
              <Link to="/clan/obvestila" className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium text-green-600 hover:underline" data-id="element-1629" data-mp="28hjtzf64hucc">
                Vsa obvestila
                <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-1630" data-mp="25x41loc444s7" />
              </Link>
            </Card>}

          <Card className="divide-y divide-line" data-id="element-1631" data-mp="20ysrdchwns9g">
            <button onClick={() => navigate('/clan/gradiva')} className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1632" data-mp="20vwp5bz0yhf7">
              <FolderOpenIcon className="h-5 w-5 text-green-600" data-id="element-1633" data-mp="1myhyy50y65m6" />
              <span className="flex-1" data-id="element-1634" data-mp="1mzt2c3qhdbcr">
                <span className="block text-sm font-medium text-ink" data-id="element-1635" data-mp="1i6zw1bfd5uyk">Gradiva zbora</span>
                <span className="block text-[13px] text-ink-muted" data-id="element-1636" data-mp="1d7nacu08atxk">Note, posnetki, Google Drive</span>
              </span>
              <ChevronRightIcon className="h-4 w-4 text-ink-muted" data-id="element-1637" data-mp="18a4z2wyqo2me" />
            </button>
            <div className="flex items-center gap-3 px-5 py-4" data-id="element-1638" data-mp="13a8vuzxv8f7w">
              <BanknoteIcon className="h-5 w-5 text-purple-500" data-id="element-1639" data-mp="z8dujvfxq1u4" />
              <span className="flex-1" data-id="element-1640" data-mp="z8xbp9xrgm73">
                <span className="block text-sm font-medium text-ink" data-id="element-1641" data-mp="onjx50bfu62k">
                  Članarina {settings.letoClanarine}
                </span>
                <span className="block text-[13px] text-ink-muted" data-id="element-1642" data-mp="tn6dvjqh64g4">
                  {clanarina?.znesek ?? settings.znesekClanarine} € · plačano{' '}
                  {clanarina?.datumPlacila ? formatDate(clanarina.datumPlacila) : '—'}
                </span>
              </span>
              <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-1643" data-mp="18cc7ms6jn2gm">
                Poravnano
              </Badge>
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-5" data-id="element-1644" data-mp="ykewflfjcahw">
        <div className="flex items-center justify-between gap-3" data-id="element-1645" data-mp="yl7u7wwinpnw">
          <h3 className="text-base font-semibold text-ink" data-id="element-1646" data-mp="13jj3a5ezlw52">Prihajajoči dogodki</h3>
          <Link to="/clan/dogodki" className="text-[13px] font-medium text-green-600 hover:underline" data-id="element-1647" data-mp="1r9lrubjp0jyx">
            Vsi dogodki
          </Link>
        </div>
        <ul className="mt-3 divide-y divide-line" data-id="element-1648" data-mp="1w2yh8nw74usq">
          {mojiDogodki.slice(1, 5).map(e => <li key={e.id} data-id="element-1649" data-mp="25dkuk68k0m87">
              <Link to={`/clan/dogodki/${e.id}`} className="flex items-center gap-3 py-3 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1650" data-mp="21ec1l3lc8sdp">
                <span className="w-16 shrink-0 text-[13px] font-medium text-ink-muted" data-id="element-1651" data-mp="2a6npahp2i1pz">
                  {formatShort(e.datum)}
                </span>
                <span className="min-w-0 flex-1" data-id="element-1652" data-mp="1mhbejde3dllk">
                  <span className="block truncate text-sm font-medium text-ink" data-id="element-1653" data-mp="1ifhlnra51lvy">{eventTitle(e)}</span>
                  <span className="block truncate text-[13px] text-ink-muted" data-id="element-1654" data-mp="1wfik702xkh89">
                    {e.zacetek} · {e.lokacija}
                  </span>
                </span>
                <Badge className={eventTypeClass[e.type]} data-id="element-1655" data-mp="1ri8x3r30khnl">{eventTypeShort[e.type]}</Badge>
              </Link>
            </li>)}
        </ul>
      </Card>

      <AbsenceModal event={naslednji ?? null} open={absenceOpen} onClose={() => setAbsenceOpen(false)} data-id="element-1656" data-mp="32ujhpa7lt7gy" />
    </div>;
}